/**
 * Route preloader — prefetches lazy-loaded page chunks when links are hovered/focused.
 * This eliminates the loading spinner when navigating between pages.
 *
 * Usage: import and call initRoutePreloader() once in your app entry point.
 */

// Map of paths to their dynamic import functions
const ROUTE_IMPORTS = {
  '/main-dashboard':              () => import('../pages/main-dashboard'),
  '/food-calendar-view':          () => import('../pages/food-calendar-view'),
  '/delivery-status':             () => import('../pages/delivery-status'),
  '/meals-management':            () => import('../pages/meals-management'),
  '/meal-detail-view':            () => import('../pages/meal-detail-view'),
  '/ingredients-management':      () => import('../pages/ingredients-management'),
  '/cost-analytics':              () => import('../pages/cost-analytics'),
  '/cost-analytics/breakdown':    () => import('../pages/cost-analytics/CostBreakdownPage'),
  '/compliance-reporting':        () => import('../pages/compliance-reporting'),
  '/audit-logs':                  () => import('../pages/audit-logs'),
  '/community':                   () => import('../pages/community'),
  '/help':                        () => import('../pages/help'),
  '/admin/dashboard':             () => import('../pages/admin/admin-dashboard'),
  '/admin/user-management':       () => import('../pages/admin/user-management'),
  '/admin/care-home-management':  () => import('../pages/admin/care-home-management'),
  '/admin/resident-management':   () => import('../pages/admin/resident-management'),
  '/super-admin':                 () => import('../pages/admin/super-admin/SuperAdminPortal'),
  '/super-admin/meal-approvals':  () => import('../pages/admin/super-admin/MealApprovals'),
  '/super-admin/user-activity':   () => import('../pages/admin/super-admin/UserActivityTracker'),
};

// Cache to avoid re-fetching already-loaded chunks
const loaded = new Set();

function preloadRoute(path) {
  if (loaded.has(path)) return;
  const importFn = ROUTE_IMPORTS[path];
  if (importFn) {
    loaded.add(path);
    importFn().catch(() => loaded.delete(path)); // retry on failure
  }
}

/**
 * Installs a single delegated event listener on the document to detect
 * mouseover/focusin on <a> elements and prefetch the matching route chunk.
 */
export function initRoutePreloader() {
  const handle = (e) => {
    const anchor = e.target.closest('a[href]');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (href && href.startsWith('/')) {
      // Strip query/hash
      const path = href.split(/[?#]/)[0];
      preloadRoute(path);
    }
  };

  document.addEventListener('mouseover', handle, { passive: true });
  document.addEventListener('focusin', handle, { passive: true });
}

/**
 * Preload the most common routes after initial page load (idle time).
 */
export function preloadCriticalRoutes() {
  const critical = ['/main-dashboard', '/food-calendar-view', '/meals-management', '/delivery-status'];
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => critical.forEach(preloadRoute), { timeout: 3000 });
  } else {
    setTimeout(() => critical.forEach(preloadRoute), 2000);
  }
}
